import { existsSync, statSync } from "node:fs";
import { homedir } from "node:os";
import { dirname, isAbsolute, resolve } from "node:path";
import { pathToFileURL } from "node:url";
import { defineConfig } from "./define-config.js";
import { ConfigLoadError, ErrorCode, isPlatformError } from "./errors.js";
import type { Config, FunctionDef } from "./types.js";

/**
 * Default file names tried (in order) when {@link loadConfigFromFile} is called without an
 * explicit path. We accept `.ts` first because that's the documented format; `.mjs` and `.js`
 * fall out for free since jiti handles all of them.
 */
export const DEFAULT_CONFIG_FILENAMES = [
	"neon.ts",
	"neon.mts",
	"neon.js",
	"neon.mjs",
] as const;

export interface LoadConfigOptions {
	/** Explicit absolute or cwd-relative path to a config file. Takes precedence over the search. */
	path?: string;
	/** Starting directory for the upward search. Defaults to `process.cwd()`. */
	cwd?: string;
	/**
	 * Hard ceiling for the upward walk — once `current === stopAt` the search returns
	 * `null` even if no `.git` boundary was hit. Defaults to the OS home directory so
	 * stray runs from outside any repo never leak into the user's `~` files.
	 */
	stopAt?: string;
	/**
	 * `"error"` (default) prevents deploy and dev from proceeding without
	 * function secrets.
	 * `"omit"` is for metadata-only reads because apply or deploy would lose the
	 * omitted keys.
	 */
	unsetFunctionEnv?: "error" | "omit";
}

/**
 * Load a `neon.ts` (or any other supported extension) and return the validated {@link Config}.
 *
 * Behavior:
 * - When `path` is set, that file is loaded directly. The file must exist and must default-export
 *   a value produced by `defineConfig()`.
 * - When `path` is omitted, we walk up from `cwd` picking the **closest** file matching
 *   {@link DEFAULT_CONFIG_FILENAMES}. The walk is monorepo-friendly: intermediate
 *   `package.json` files do **not** stop it, so a single `neon.ts` lifted to the workspace
 *   root keeps working when invoked from inside any sub-package. The walk terminates at the
 *   first directory containing `.git`, at `stopAt`, or at the filesystem root.
 *
 * jiti is loaded lazily so that callers who pass an already-resolved `Config` to `pushConfig`
 * never pay the import cost.
 */
export async function loadConfigFromFile(
	options: LoadConfigOptions = {},
): Promise<{
	config: Config;
	resolvedPath: string;
}> {
	// The omit reload replaces process.env, which is process-global, including
	// across duplicate @neon/config copies. Queue loads on globalThis.
	return await withSerializedConfigLoad(() =>
		loadConfigFromFileUnlocked(options),
	);
}

async function loadConfigFromFileUnlocked(options: LoadConfigOptions): Promise<{
	config: Config;
	resolvedPath: string;
}> {
	if (options.unsetFunctionEnv !== "omit") {
		return await loadConfigOnce(options);
	}

	try {
		return await loadConfigOnce(options);
	} catch (err) {
		const keys = unsetFunctionEnvKeys(err);
		if (keys === null) throw err;
		// The user's neon.ts may resolve another @neon/config copy, so the reload
		// input must satisfy its validation.
		return await withPlaceholderFunctionEnv(async () => {
			const loaded = await loadConfigOnce(options);
			return {
				...loaded,
				config: withoutFunctionEnvKeys(loaded.config, new Set(keys)),
			};
		});
	}
}

async function loadConfigOnce(options: LoadConfigOptions): Promise<{
	config: Config;
	resolvedPath: string;
}> {
	const resolvedPath = options.path
		? resolveExplicitPath(options.path, options.cwd)
		: findDefaultConfig(options.cwd, options.stopAt);

	if (!resolvedPath) {
		throw new ConfigLoadError(
			[
				`Could not find a Neon config file while walking up from ${resolve(options.cwd ?? process.cwd())}.`,
				`Looked for: ${DEFAULT_CONFIG_FILENAMES.join(", ")} (stopping at the first directory with a \`.git\`).`,
				`Create one at your repository root (or anywhere on the path from cwd up to .git), or pass an explicit \`configPath\` (SDK) / \`--config <path>\` (CLI).`,
			].join("\n"),
		);
	}

	let mod: unknown;
	try {
		mod = await importModule(
			resolvedPath,
			options.unsetFunctionEnv === "omit",
		);
	} catch (cause) {
		// `defineConfig()` runs at module-eval time, so a config the user got *wrong*
		// (a bad function slug, an unknown key, an invalid duration, …) throws a
		// PlatformError from inside this import. That error already pinpoints the exact
		// field and reason, so surface it verbatim — burying it under the generic
		// "this is usually a TypeScript syntax error" hint sent users hunting for a
		// syntax bug that isn't there. The hint is reserved for genuine evaluation
		// failures (syntax errors, missing deps, thrown runtime exceptions).
		if (isPlatformError(cause)) {
			throw cause;
		}
		throw new ConfigLoadError(
			[
				`Failed to evaluate ${resolvedPath}.`,
				`Underlying error: ${cause instanceof Error ? cause.message : String(cause)}`,
				"This is usually a TypeScript syntax error, a missing dependency, or a runtime exception inside the config file. Run the file directly (e.g. `npx tsx neon.ts`) to reproduce.",
			].join("\n"),
			{ cause },
		);
	}

	const exported = extractDefaultExport(mod);
	if (exported === undefined) {
		throw new ConfigLoadError(
			[
				`${resolvedPath} loaded successfully but did not default-export a config.`,
				"Add `export default defineConfig({ ... })` at the bottom of the file. (Named exports are ignored.)",
			].join("\n"),
		);
	}

	// Run through defineConfig to validate any function the user might have constructed manually.
	const config = defineConfig(exported as Config);
	return { config, resolvedPath };
}

function resolveExplicitPath(input: string, cwd?: string): string {
	const base = resolve(cwd ?? process.cwd());
	const abs = isAbsolute(input) ? input : resolve(base, input);
	if (!existsSync(abs)) {
		throw new ConfigLoadError(
			`Config file not found at ${abs}. The path was resolved from \`${input}\` against ${base}.`,
		);
	}
	const s = statSync(abs);
	if (!s.isFile()) {
		throw new ConfigLoadError(
			`Config path ${abs} is a directory, not a file. Pass a path to the file itself (e.g. ./neon.ts).`,
		);
	}
	return abs;
}

function findDefaultConfig(
	cwd: string | undefined,
	stopAt: string | undefined,
): string | null {
	let current = resolve(cwd ?? process.cwd());
	const stop = resolve(stopAt ?? homedir());
	let lastSeen: string | null = null;

	while (true) {
		for (const name of DEFAULT_CONFIG_FILENAMES) {
			const candidate = resolve(current, name);
			if (existsSync(candidate) && safeIsFile(candidate))
				return candidate;
		}

		// `.git` is the canonical repo-root marker. `package.json` is deliberately *not*
		// a stop: monorepos lift `neon.ts` above sub-package package.jsons.
		if (existsSync(resolve(current, ".git"))) return null;
		if (current === stop) return null;

		const parent = dirname(current);
		if (parent === current || parent === lastSeen) return null;
		lastSeen = current;
		current = parent;
	}
}

async function importModule(
	absPath: string,
	forceJiti = false,
): Promise<unknown> {
	const lower = absPath.toLowerCase();
	const needsJiti =
		forceJiti ||
		lower.endsWith(".ts") ||
		lower.endsWith(".mts") ||
		lower.endsWith(".cts");

	if (!needsJiti) {
		return import(pathToFileURL(absPath).href);
	}

	const jitiModule: unknown = await import("jiti");
	const createJiti = extractCreateJiti(jitiModule);
	if (!createJiti) {
		throw new ConfigLoadError(
			[
				"jiti is required to load TypeScript config files but could not be initialised.",
				"Reinstall the package dependencies (`pnpm install` / `npm install`) — jiti is a runtime dependency of @neon/config.",
			].join(" "),
		);
	}
	const jiti = createJiti(pathToFileURL(absPath).href, {
		interopDefault: true,
		moduleCache: false,
	});
	return jiti.import(absPath);
}

function extractCreateJiti(
	mod: unknown,
): ((id: string, options?: unknown) => JitiInstance) | null {
	if (mod === null || typeof mod !== "object") return null;
	const obj = mod as Record<string, unknown>;
	if (typeof obj.createJiti === "function") {
		return obj.createJiti as (
			id: string,
			options?: unknown,
		) => JitiInstance;
	}
	const def = obj.default;
	if (def !== null && typeof def === "object") {
		const defObj = def as Record<string, unknown>;
		if (typeof defObj.createJiti === "function") {
			return defObj.createJiti as (
				id: string,
				options?: unknown,
			) => JitiInstance;
		}
	}
	return null;
}

interface JitiInstance {
	import(id: string): Promise<unknown>;
}

function extractDefaultExport(mod: unknown): unknown {
	if (mod === null || typeof mod !== "object") return mod;
	const obj = mod as Record<string, unknown>;
	if ("default" in obj && obj.default !== undefined) return obj.default;
	// No `default` export. If the module itself is a function, treat it as the config —
	// that lets tests and advanced users skip the wrapper.
	// Otherwise, return `undefined` so the caller surfaces a clear ConfigLoadError.
	if (typeof mod === "function") return mod;
	return undefined;
}

function safeIsFile(path: string): boolean {
	try {
		return statSync(path).isFile();
	} catch {
		return false;
	}
}

const UNSET_FUNCTION_ENV_ISSUE =
	/^preview\.functions\.[^.]+\.env\.(.+): Environment variable "\1" for function ".+" is undefined/;

function unsetFunctionEnvKeys(err: unknown): string[] | null {
	if (!isPlatformError(err) || err.code !== ErrorCode.InvalidConfig) {
		return null;
	}
	const issues = platformErrorIssues(err);
	if (issues === null || issues.length === 0) return null;
	const keys: string[] = [];
	for (const issue of issues) {
		const match = UNSET_FUNCTION_ENV_ISSUE.exec(issue);
		const key = match?.[1];
		if (key === undefined) return null;
		keys.push(key);
	}
	return keys;
}

function platformErrorIssues(err: unknown): readonly string[] | null {
	if (typeof err !== "object" || err === null || !("issues" in err)) {
		return null;
	}
	const { issues } = err;
	if (
		!Array.isArray(issues) ||
		issues.some((issue) => typeof issue !== "string")
	) {
		return null;
	}
	return issues;
}

async function withPlaceholderFunctionEnv<T>(
	run: () => Promise<T>,
): Promise<T> {
	// Validation reports config keys, not source process.env names, so the reload
	// must cover every unset lookup.
	const original = process.env;
	const proxy = new Proxy(original, {
		get(target, prop, receiver) {
			if (typeof prop === "symbol") {
				return Reflect.get(target, prop, receiver);
			}
			const value = Reflect.get(target, prop, receiver);
			return value === undefined ? "" : value;
		},
	});
	process.env = proxy;
	try {
		return await run();
	} finally {
		process.env = original;
	}
}

const CONFIG_LOAD_LOCK = Symbol.for("@neon/config loadConfigFromFile lock");

type ConfigLoadLock = { chain: Promise<unknown> };

function configLoadLock(): ConfigLoadLock {
	const g = globalThis as typeof globalThis & {
		[CONFIG_LOAD_LOCK]?: ConfigLoadLock;
	};
	const lock = g[CONFIG_LOAD_LOCK] ?? { chain: Promise.resolve() };
	g[CONFIG_LOAD_LOCK] = lock;
	return lock;
}

async function withSerializedConfigLoad<T>(run: () => Promise<T>): Promise<T> {
	const lock = configLoadLock();
	const next = lock.chain.then(run, run);
	lock.chain = next.then(
		() => undefined,
		() => undefined,
	);
	return next;
}

function withoutFunctionEnvKeys(
	config: Config,
	keys: ReadonlySet<string>,
): Config {
	const functions = config.preview?.functions;
	if (!functions) return config;
	const nextFunctions: Record<string, FunctionDef> = {};
	for (const [slug, fn] of Object.entries(functions)) {
		if (!fn.env) {
			nextFunctions[slug] = fn;
			continue;
		}
		const env = Object.fromEntries(
			Object.entries(fn.env).filter(([key]) => !keys.has(key)),
		);
		nextFunctions[slug] = { ...fn, env };
	}
	return Object.freeze({
		...config,
		preview: { ...config.preview, functions: nextFunctions },
	});
}
